import React, { useEffect } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	CircularProgress,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Alert,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { Client } from "../graphql/querys/client";
import { RentingByCostumer } from "../graphql/querys/RentingByCustomer";
import { toReadableDate } from "./Machine";
import { exportToExcel } from "../helpers/excell";
import { useTranslation } from "react-i18next";

// Function to calculate total price
export const calculateTotalPrice = (pricePerDay, startedRent, endedRent) => {
	const startDate = new Date(startedRent);
	const endDate = endedRent ? new Date(endedRent) : new Date();
	const timeDiff = Math.abs(endDate - startDate);
	const numberOfDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
	return pricePerDay * numberOfDays;
};

export default function ClientScreen() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { t } = useTranslation();

	// Fetch client data
	const {
		loading: loadingClient,
		error: errorClient,
		data: dataClient,
	} = useQuery(Client, {
		variables: { id },
		onError: (error) => {
			console.error("Client query error:", error);
			// If it's an invalid ID error, show a specific message
			if (error.message.includes("Invalid ID format") ||
				error.message.includes("Argument passed in must be a single String of 12 bytes")) {
				navigate("/clients", {
					state: {
						error: "Invalid client ID format. Please select a valid client."
					}
				});
			}
		}
	});

	// Fetch renting data
	const {
		loading: loadingRenting,
		error: errorRenting,
		data: dataRenting,
		refetch,
	} = useQuery(RentingByCostumer, { variables: { id } });

	useEffect(() => {
		refetch();
	}, [refetch]);

	// Handle loading state
	if (loadingClient || loadingRenting) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "80vh",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	// Handle error state
	if (errorClient || errorRenting) {
		return (
			<Box
				sx={{
					m: 2,
				}}
			>
				<Alert severity="error">
					{errorClient?.message || errorRenting?.message || t("ERROR.LOAD_GENERIC")}
				</Alert>
			</Box>
		);
	}

	// Destructure client data
	const client = dataClient?.getUser;

	return (
		<Box
			sx={{
				p: { xs: 2, md: 4 },
				maxWidth: "1200px",
				margin: "0 auto",
			}}
		>
			{/* Client Information */}
			<Card
				sx={{
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					alignItems: "center",
					p: 3,
					boxShadow: 3,
					borderRadius: 2,
					mb: 4,
				}}
			>
				{/* Client Avatar */}
				<CardMedia
					component="img"
					image={client.avatar}
					alt={`${client.name} avatar`}
					sx={{
						width: { xs: "100%", md: "250px" },
						height: "auto",
						borderRadius: 2,
						objectFit: "cover",
						mb: { xs: 2, md: 0 },
						mr: { md: 4 },
					}}
				/>
				{/* Client Details */}
				<CardContent
					sx={{
						textAlign: { xs: "center", md: "left" },
					}}
				>
					<Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
						{client.name}
					</Typography>
					<Typography variant="body1" color="textSecondary" gutterBottom>
						{client.phoneNumber || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						<strong>{t("FIELDS.email")}:</strong> {client.email || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						<strong>{t("FIELDS.nif")}:</strong> {client.nif || client.customer_tax_registration_number || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						<strong>{t("FIELDS.address")}:</strong> {client.addressInfo?.Address || client.addressInfo?.address || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						<strong>{t("FIELDS.postalCode")}:</strong> {client.addressInfo?.postalCode || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						<strong>{t("FIELDS.localidade")}:</strong> {client.localidade || "-"}
					</Typography>
				</CardContent>
			</Card>

			{/* Renting Information */}
			<Box>
				<Grid container direction="column" alignItems="center">
					{dataRenting && dataRenting.rentingsByUser.length > 0 ? (
						<Grid item xs={12} md={10} sx={{ width: "100%" }}>
							{/* Export Button */}
							<Button
								variant="contained"
								color="primary"
								onClick={() =>
									dataRenting &&
									exportToExcel(dataRenting.rentingsByUser, "RentingData")
								}
								sx={{
									mb: 2,
								}}
							>
								{t("ACTIONS.EXPORT_EXCEL")}
							</Button>

							{/* Renting Table */}
							<TableContainer component={Paper} sx={{ borderRadius: 2 }}>
								<Table>
									<TableHead
										sx={{
											backgroundColor: "primary.main",
										}}
									>
										<TableRow>
											<TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
												{t("FIELDS.equipment")}
											</TableCell>
											<TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
												{t("FIELDS.pricePerDayEuro")}
											</TableCell>
											<TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
												{t("FIELDS.startDate")}
											</TableCell>
											<TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
												{t("FIELDS.expectedEndDate")}
											</TableCell>
											<TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
												{t("FIELDS.realEndDate")}
											</TableCell>
											<TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
												{t("FIELDS.generated")} (€)
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{dataRenting.rentingsByUser
											.slice()
											.sort((a, b) => (a.endedRent ? 1 : -1))
											.map((rent) => {
												const totalPrice = calculateTotalPrice(
													rent.pricePerDay,
													rent.startedRent,
													rent.endedRent
												);
												const isOngoing = !rent.endedRent;
												return (
													<TableRow
														key={rent._id}
														hover
														sx={{
															backgroundColor: isOngoing
																? "rgba(250, 230, 127, 0.3)"
																: "inherit",
															cursor: "pointer",
														}}
														onClick={() => navigate(`/machines/${rent.machineInfo._id}`)}
													>
														<TableCell>{rent.machineInfo.name}</TableCell>
														<TableCell>{rent.pricePerDay.toFixed(2)} €</TableCell>
														<TableCell>{toReadableDate(rent.startedRent) || "-"}</TableCell>
														<TableCell>
															{toReadableDate(rent.previsionEndingRent) || "-"}
														</TableCell>
														<TableCell>
															{rent.endedRent ? toReadableDate(rent.endedRent) : "-"}
														</TableCell>
														<TableCell>{totalPrice.toFixed(2)} €</TableCell>
													</TableRow>
												);
											})}
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>
					) : (
						<Typography variant="h6" color="textSecondary">
							{t("CLIENT.NO_RENTINGS")}
						</Typography>
					)}
				</Grid>
			</Box>
		</Box>
	);
}
